import { useState } from "react";
import type { TossPaymentData } from "@/types/toss-payments";
import { isTossPaymentData, hasTossPaymentCard, hasTossPaymentReceipt, hasTossPaymentEasyPay } from "@/types/toss-payments";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Calendar, MapPin, Users, Clock, CreditCard, Bus, Phone, Mail, AlertCircle, Loader2, Receipt, Banknote, Info, Edit, X, MoreHorizontal, RefreshCw, Shield, FileText, ExternalLink, ShieldCheck } from "lucide-react";
import { BackButton } from "@/components/ui/back-button";
import { ScheduleTimeline } from "@/components/ui/schedule-timeline";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useBookingDetail, canCancelBooking, canModifyBooking, useCancellationDetails } from "@/hooks/useBookingDetail";
import { useBookingCancel } from "@/hooks/useBookingCancel";
import { LazyCancelBookingDialog, LazyEditBookingModal } from "@/components/LazyModals";
const BookingDetail = () => {
  const {
    bookingId
  } = useParams<{
    bookingId: string;
  }>();
  const navigate = useNavigate();
  const {
    data: booking,
    isLoading,
    error,
    refetch
  } = useBookingDetail(Number(bookingId));
  const {
    data: cancellationDetails
  } = useCancellationDetails(Number(bookingId));
  const {
    mutate: cancelBooking,
    isPending: isCancelling
  } = useBookingCancel();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: {
        label: '결제 대기',
        variant: 'secondary' as const
      },
      confirmed: {
        label: '예약 완료',
        variant: 'default' as const
      },
      completed: {
        label: '여행 완료',
        variant: 'outline' as const
      },
      cancelled: {
        label: '예약 취소',
        variant: 'destructive' as const
      }
    };
    return statusConfig[status as keyof typeof statusConfig] || {
      label: status,
      variant: 'secondary' as const
    };
  };
  if (isLoading) {
    return <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-20">
            <Loader2 className="w-16 h-16 text-brand mx-auto animate-spin mb-4" />
            <h2 className="text-h3 font-bold text-gray-800 font-korean">예약 정보를 불러오는 중...</h2>
          </div>
        </div>
        <Footer />
      </div>;
  }
  if (error || !booking) {
    return <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-20">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-h3 font-bold text-gray-800 mb-2 font-korean">오류 발생</h2>
            <p className="text-red-600 bg-red-100 p-4 rounded-md">
              {error?.message || '예약 정보를 찾을 수 없습니다.'}
            </p>
            <Button onClick={() => navigate('/booking-history')} className="mt-4">
              예약 내역으로 돌아가기
            </Button>
          </div>
        </div>
        <Footer />
      </div>;
  }
  const course = booking.trip.product.product_courses[0]?.course;
  const statusInfo = getStatusBadge(booking.status);
  const cancelInfo = canCancelBooking(booking);
  const modifyInfo = canModifyBooking(booking);

  // Get schedule data from product schedule_template or fallback to mock data
  const scheduleData = booking.trip.product.schedule_template?.schedule_items || [{
    time: booking.trip.departure_time || "09:00",
    location: booking.trip.departure_point || course?.start_point || "출발 장소",
    map_link: "https://map.naver.com/"
  }, {
    time: "12:00",
    location: "중간 휴게소",
    map_link: "https://map.naver.com/"
  }, {
    time: "15:30",
    location: course?.end_point || "도착 지점",
    map_link: "https://map.naver.com/"
  }];

  const handleCancelBooking = (cancelReason: string) => {
    cancelBooking({
      bookingId: booking.booking_id,
      cancelReason
    }, {
      onSuccess: () => {
        setShowCancelDialog(false);
        // 취소 완료 후 예약 내역 페이지로 이동
        setTimeout(() => {
          navigate('/booking-history');
        }, 2000);
      }
    });
  };
  return <div className="min-h-screen bg-background">
      <Header />
      
      {/* 헤더 영역 */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <BackButton onClick={() => navigate('/booking-history')} className="mb-3">
            예약 내역으로
          </BackButton>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-h2 font-bold text-gray-800 font-korean">{course?.name}</h1>
              <p className="text-body-medium text-gray-600 font-korean">예약번호: {booking.order_id}</p>
            </div>
            
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Content - Left Column */}
          <div className="md:col-span-2 space-y-6">
        
        {/* 여행 정보 카드 */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-body-medium font-korean">
              <Calendar className="w-4 h-4 text-brand" />
              여행 일정
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <p className="text-body-small font-medium text-gray-700 font-korean">출발일</p>
                <p className="text-body-medium font-medium font-korean">
                  {new Date(booking.trip.departure_date).toLocaleDateString('ko-KR', {
                      month: 'long',
                      day: 'numeric',
                      weekday: 'short'
                    })}
                  {booking.trip.departure_time && <span className="text-body-small text-gray-600 ml-2">
                    {booking.trip.departure_time}
                  </span>}
                </p>
              </div>
              <div>
                <p className="text-body-small font-medium text-gray-700 font-korean">집합 장소</p>
                <p className="text-body-small text-gray-600 font-korean">
                  {booking.trip.departure_point || course?.start_point || '추후 안내'}
                </p>
              </div>
            </div>
            
            {course && <>
                <Separator className="my-2" />
                <div className="grid grid-cols-3 gap-3 text-caption">
                  <div>
                    <span className="text-gray-600">거리: </span>
                    <span className="font-medium">
                      {course.length ? `${course.length}km` : '미정'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">소요: </span>
                    <span className="font-medium">
                      {course.estimated_time ? 
                        (course.estimated_time >= 60 
                          ? `${Math.floor(course.estimated_time / 60)}시간${course.estimated_time % 60 > 0 ? ` ${course.estimated_time % 60}분` : ''}`
                          : `${course.estimated_time}분`)
                        : '미정'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">난이도: </span>
                    <span className="font-medium">{course.difficulty || '미정'}</span>
                  </div>
                </div>
              </>}
          </CardContent>
        </Card>

        {/* 여행 일정 타임라인 */}
        <ScheduleTimeline scheduleItems={scheduleData} className="" />

        {/* 참가자 정보 카드 */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-brand" />
              참가자 정보
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {booking.booking_participants.sort((a, b) => a.participant_order - b.participant_order).map(participant => <div key={participant.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-brand text-white rounded-full flex items-center justify-center text-body-small font-medium">
                        {participant.participant_order}
                      </div>
                      <div>
                        <p className="font-medium">{participant.name}</p>
                        <div className="flex gap-4 text-body-small text-gray-600">
                          {participant.phone && <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {participant.phone}
                            </span>}
                          {participant.email && <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {participant.email}
                            </span>}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-body-small text-gray-600 font-korean">좌석</p>
                      <p className="font-medium flex items-center justify-end gap-2">
                        {participant.seat ? `${participant.seat}번` : '미배정'}
                        {modifyInfo.canModify && modifyInfo.hoursUntilDeparture >= 24 && (
                          <span className="text-caption text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded font-korean">
                            변경가능
                          </span>
                        )}
                      </p>
                    </div>
                  </div>)}
            </div>
          </CardContent>
        </Card>

        {/* 교통 정보 카드 */}
        {(booking.trip.bus_number || booking.trip.driver_name) && <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bus className="w-5 h-5 text-brand" />
                교통 정보
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {booking.trip.bus_number && <div>
                    <p className="font-medium text-gray-800">버스 번호</p>
                    <p className="text-gray-600">{booking.trip.bus_number}</p>
                  </div>}
                {booking.trip.driver_name && <div>
                    <p className="font-medium text-gray-800">기사님</p>
                    <p className="text-gray-600">
                      {booking.trip.driver_name}
                      {booking.trip.driver_phone && <span className="ml-2 text-body-small">({booking.trip.driver_phone})</span>}
                    </p>
                  </div>}
                {booking.trip.bus_guide && <div className="md:col-span-2">
                    <p className="font-medium text-gray-800">가이드</p>
                    <p className="text-gray-600">{booking.trip.bus_guide}</p>
                  </div>}
              </div>
            </CardContent>
          </Card>}

        {/* 결제 정보 카드 */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-brand" />
              결제 정보
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">총 결제금액</span>
                <span className="font-bold text-body-large font-korean">{booking.total_price.toLocaleString()}원</span>
              </div>
              <Separator />
              <div className="flex justify-between items-center text-body-small">
                <span className="text-gray-600 font-korean">결제일시</span>
                <span>{new Date(booking.updated_at).toLocaleString('ko-KR')}</span>
              </div>
              
              {/* 토스페이먼츠 결제 정보 표시 */}
              {booking.toss_payment_data && isTossPaymentData(booking.toss_payment_data) && (
                <>
                  {/* 결제 수단 */}
                  <div className="flex justify-between items-center text-body-small">
                    <span className="text-gray-600 font-korean">결제 수단</span>
                    <span className="font-medium">
                      {booking.toss_payment_data?.method || '카드'}
                      {hasTossPaymentEasyPay(booking.toss_payment_data) && booking.toss_payment_data.easyPay?.provider && (
                        <span className="ml-1 text-caption text-gray-500">
                          ({booking.toss_payment_data.easyPay?.provider})
                        </span>
                      )}
                    </span>
                  </div>
                  
                  {/* 카드 승인번호 */}
                  {hasTossPaymentCard(booking.toss_payment_data) && booking.toss_payment_data.card?.approveNo && (
                    <div className="flex justify-between items-center text-body-small">
                      <span className="text-gray-600 flex items-center gap-1 font-korean">
                        <ShieldCheck className="w-3 h-3" />
                        승인번호
                      </span>
                      <span className="font-mono text-caption">
                        {booking.toss_payment_data.card?.approveNo}
                      </span>
                    </div>
                  )}
                  
                  {/* 카드 정보 */}
                  {hasTossPaymentCard(booking.toss_payment_data) && booking.toss_payment_data.card?.number && (
                    <div className="flex justify-between items-center text-body-small">
                      <span className="text-gray-600 font-korean">카드번호</span>
                      <span className="font-mono text-caption">
                        {booking.toss_payment_data.card?.number}
                      </span>
                    </div>
                  )}
                  
                  {/* 영수증 링크 */}
                  {hasTossPaymentReceipt(booking.toss_payment_data) && (
                    <div className="pt-3 mt-3 border-t">
                      <a
                        href={booking.toss_payment_data.receipt?.url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 px-3 py-2 text-body-small text-brand hover:text-brand-dark bg-brand/5 hover:bg-brand/10 rounded-lg transition-colors font-korean"
                      >
                        <Receipt className="w-4 h-4" />
                        전자영수증 보기
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                </>
              )}
              
              {/* 예약번호 (fallback) */}
              {(!booking.toss_payment_data || !isTossPaymentData(booking.toss_payment_data)) && booking.order_id && (
                <div className="flex justify-between items-center text-body-small">
                  <span className="text-gray-600 font-korean">예약번호</span>
                  <span className="font-mono text-caption">{booking.order_id}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>


            {/* 모바일용 액션 버튼 영역 */}
            <Card className="md:hidden">
              <CardContent className="p-4">
                <div className="flex flex-col gap-3">
                  {booking.status === 'confirmed' && <>
                      {modifyInfo.canModify ? (
                        <div className="space-y-2">
                          <Button className="w-full" onClick={() => setShowEditModal(true)}>
                            <Edit className="w-4 h-4 mr-2" />
                            예약 수정
                          </Button>
                          {modifyInfo.hoursUntilDeparture < 24 && (
                            <p className="text-caption text-gray-500 text-center font-korean">
                              * 출발 24시간 이내에는 좌석 변경이 불가능합니다
                            </p>
                          )}
                        </div>
                      ) : (
                        <Button disabled className="w-full" title={modifyInfo.reason}>
                          예약 수정 ({modifyInfo.reason})
                        </Button>
                      )}

                      {cancelInfo.canCancel ? <Button variant="destructive" className="w-full" onClick={() => setShowCancelDialog(true)} disabled={isCancelling}>
                          {isCancelling ? <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              취소 처리 중...
                            </> : '예약 취소'}
                        </Button> : <Button variant="destructive" disabled className="w-full" title={cancelInfo.reason}>
                          예약 취소 ({cancelInfo.reason})
                        </Button>}
                    </>}
                  
                  <Button variant="outline" onClick={() => navigate('/booking-history')} className="w-full">
                    예약 내역으로 돌아가기
                  </Button>
                </div>
              </CardContent>
            </Card>

        {/* 취소 내역 (취소된 예약인 경우만 표시) */}
        {booking.status === 'cancelled' && (booking.cancellation?.[0] || cancellationDetails) && <Card className="border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-800">
                <Receipt className="w-5 h-5" />
                취소 내역
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(() => {
                const cancelData = booking.cancellation?.[0] || cancellationDetails;
                if (!cancelData) return null;
                return <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <p className="text-body-small font-medium text-red-700 font-korean">취소 일시</p>
                        <p className="text-red-800">
                          {new Date(cancelData.cancelled_at).toLocaleString('ko-KR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-body-small font-medium text-red-700 font-korean">취소 사유</p>
                        <p className="text-red-800">{cancelData.cancel_reason}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="bg-white p-3 rounded-lg border border-red-200">
                        <h4 className="font-medium text-red-800 mb-2 flex items-center gap-1">
                          <Banknote className="w-4 h-4" />
                          환불 정보
                        </h4>
                        <div className="space-y-1 text-body-small">
                          <div className="flex justify-between">
                            <span className="text-gray-600 font-korean">원 결제금액:</span>
                            <span className="font-medium">{Number(cancelData.original_amount).toLocaleString()}원</span>
                          </div>
                          {Number(cancelData.cancel_fee) > 0 && <div className="flex justify-between text-red-600">
                              <span className="font-korean">취소 수수료:</span>
                              <span className="font-medium">-{Number(cancelData.cancel_fee).toLocaleString()}원</span>
                            </div>}
                          <Separator />
                          <div className="flex justify-between font-medium text-green-600">
                            <span className="font-korean">환불 금액:</span>
                            <span>{Number(cancelData.refund_amount).toLocaleString()}원</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>;
              })()}
              
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-body-small text-blue-800 flex items-start gap-2 font-korean">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  환불은 영업일 기준 3-5일 내에 결제 수단으로 처리됩니다. 
                  환불 처리 현황은 결제사(토스페이먼츠)에서 확인하실 수 있습니다.
                </p>
              </div>
            </CardContent>
          </Card>}

          </div>

          {/* Sidebar - Right Column */}
          <div className="hidden md:block">
            <div className="sticky top-6 space-y-4">
              {/* Booking Summary Card */}
              <Card className="border-brand/20">
                <CardHeader className="pb-3">
                  <CardTitle className="text-body-large flex items-center gap-2 font-korean">
                    <Receipt className="w-5 h-5 text-brand" />
                    예약 요약
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-left">
                    <Badge variant={statusInfo.variant} className="text-body-small px-3 py-1 font-korean">
                      {statusInfo.label}
                    </Badge>
                  </div>
                  
                  <div className="space-y-3 text-body-small">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-korean">출발일</span>
                      <span className="font-medium">
                        {new Date(booking.trip.departure_date).toLocaleDateString('ko-KR', {
                        month: 'short',
                        day: 'numeric'
                      })}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-korean">참가자</span>
                      <span className="font-medium">{booking.booking_participants.length}명</span>
                    </div>
                    
                    <Separator />
                    
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-korean">총 결제금액</span>
                      <span className="font-bold text-body-large text-brand font-korean">
                        {booking.total_price.toLocaleString()}원
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Action Buttons Card - Desktop */}
              {booking.status === 'confirmed' && (
                <Card className="border-brand/20">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-body-large font-korean">예약 관리</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {modifyInfo.canModify ? (
                      <div className="space-y-2">
                        <Button className="w-full" onClick={() => setShowEditModal(true)}>
                          <Edit className="w-4 h-4 mr-2" />
                          예약 수정
                        </Button>
                        {modifyInfo.hoursUntilDeparture < 24 && (
                          <p className="text-caption text-gray-500 text-center font-korean">
                            * 출발 24시간 이내에는 좌석 변경이 불가능합니다
                          </p>
                        )}
                      </div>
                    ) : (
                      <Button disabled className="w-full" title={modifyInfo.reason}>
                        예약 수정 ({modifyInfo.reason})
                      </Button>
                    )}

                    {cancelInfo.canCancel ? (
                      <Button 
                        variant="destructive" 
                        className="w-full" 
                        onClick={() => setShowCancelDialog(true)} 
                        disabled={isCancelling}
                      >
                        {isCancelling ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            처리 중...
                          </>
                        ) : (
                          <>
                            <X className="w-4 h-4 mr-2" />
                            예약 취소
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button 
                        disabled 
                        variant="destructive" 
                        className="w-full" 
                        title={cancelInfo.reason}
                      >
                        예약 취소 ({cancelInfo.reason})
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* 중요 안내사항 카드 */}
              <Card className="border-brand/20 bg-brand/5">
                <CardHeader className="pb-3">
                  <CardTitle className="text-body-large flex items-center gap-2 font-korean">
                    <Info className="w-5 h-5 text-brand" />
                    중요 안내사항
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* 환불 정책 */}
                  <div className="space-y-3">
                    <h5 className="font-medium text-body-small flex items-center gap-1.5 font-korean">
                      <RefreshCw className="w-4 h-4 text-gray-600" />
                      취소 및 환불 정책
                    </h5>
                    <div className="bg-white rounded-lg p-3 space-y-2 border border-brand/10">
                      <div className="flex items-center justify-between text-caption">
                        <span className="text-gray-700 font-korean">출발 4일 전까지</span>
                        <Badge className="bg-green-100 text-green-700 border-green-200 font-korean">100% 환불</Badge>
                      </div>
                      <div className="flex items-center justify-between text-caption">
                        <span className="text-gray-700 font-korean">출발 3~1일 전 오전</span>
                        <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200 font-korean">70% 환불</Badge>
                      </div>
                      <div className="flex items-center justify-between text-caption">
                        <span className="text-gray-500 text-caption ml-2 font-korean">(오전 12시 이전)</span>
                        <span></span>
                      </div>
                      <div className="flex items-center justify-between text-caption">
                        <span className="text-gray-700 font-korean">출발 1일 전 오후</span>
                        <Badge className="bg-red-100 text-red-700 border-red-200 font-korean">환불 불가</Badge>
                      </div>
                      <div className="flex items-center justify-between text-caption">
                        <span className="text-gray-500 text-caption ml-2 font-korean">(오후 12시 이후)</span>
                        <span></span>
                      </div>
                      <div className="flex items-center justify-between text-caption">
                        <span className="text-gray-700 font-korean">출발 당일</span>
                        <Badge className="bg-red-100 text-red-700 border-red-200 font-korean">환불 불가</Badge>
                      </div>
                    </div>
                    <div className="bg-blue-50 p-2 rounded-lg border border-blue-200">
                      <p className="text-caption text-blue-800 leading-relaxed font-korean">
                        <strong>100% 전액 환불 조건:</strong><br/>
                        • 천재지변 (태풍, 폭우, 폭설, 지진 등)<br/>
                        • 홀씨 측 사정으로 인한 취소<br/>
                        • 최소 출발 인원(15명) 미달<br/>
                        • 정부 지침에 따른 취소
                      </p>
                    </div>
                  </div>

                  <Separator />

                  {/* 예약 관련 안내 */}
                  <div className="space-y-3">
                    <h5 className="font-medium text-body-small flex items-center gap-1.5 font-korean">
                      <FileText className="w-4 h-4 text-gray-600" />
                      예약 관련 안내
                    </h5>
                    <ul className="text-caption text-gray-700 space-y-1.5 font-korean">
                      <li className="flex items-start gap-1">
                        <span className="text-brand">•</span>
                        <span>예약 후 <strong>24시간 이내</strong> 결제를 완료하셔야 합니다</span>
                      </li>
                      <li className="flex items-start gap-1">
                        <span className="text-brand">•</span>
                        <span>24시간 동안 선택하신 좌석이 유지됩니다</span>
                      </li>
                      <li className="flex items-start gap-1">
                        <span className="text-brand">•</span>
                        <span>예약 수정은 출발 <strong>24시간 전</strong>까지 가능합니다</span>
                      </li>
                      <li className="flex items-start gap-1">
                        <span className="text-brand">•</span>
                        <span>출발 24시간 이내에는 좌석 변경이 불가능합니다</span>
                      </li>
                    </ul>
                  </div>

                  <Separator />

                  {/* 출발 당일 안내 */}
                  <div className="space-y-3">
                    <h5 className="font-medium text-body-small flex items-center gap-1.5 font-korean">
                      <Clock className="w-4 h-4 text-gray-600" />
                      출발 당일 안내
                    </h5>
                    <ul className="text-caption text-gray-700 space-y-1.5 font-korean">
                      <li className="flex items-start gap-1">
                        <span className="text-brand">•</span>
                        <span>출발 <strong>10분 전</strong>까지 집합장소에 도착해주세요</span>
                      </li>
                      <li className="flex items-start gap-1">
                        <span className="text-brand">•</span>
                        <span>신분증을 꼭 지참해주세요</span>
                      </li>
                      <li className="flex items-start gap-1">
                        <span className="text-brand">•</span>
                        <span>날씨에 따라 일정이 변경될 수 있습니다</span>
                      </li>
                      <li className="flex items-start gap-1">
                        <span className="text-brand">•</span>
                        <span>편안한 운동화와 복장을 준비해주세요</span>
                      </li>
                    </ul>
                  </div>

                  {/* 자세한 정책 링크 */}
                  <div className="pt-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full text-caption hover:bg-brand/10 hover:border-brand font-korean"
                      onClick={() => window.open('/refund-policy', '_blank')}
                    >
                      <Shield className="w-3 h-3 mr-1.5" />
                      전체 환불 정책 보기
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />

      {/* 취소 확인 다이얼로그 */}
      <LazyCancelBookingDialog booking={booking} isOpen={showCancelDialog} isLoading={isCancelling} onClose={() => setShowCancelDialog(false)} onConfirm={handleCancelBooking} />

      {/* 예약 수정 모달 */}
      <LazyEditBookingModal booking={booking} isOpen={showEditModal} onClose={() => setShowEditModal(false)} onSuccess={() => {
      setShowEditModal(false);
      refetch(); // 예약 정보 새로고침
    }} />
    </div>;
};
export default BookingDetail;